import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Heart,
  Plus,
  Send,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  CheckCircle,
  Trash2,
  TrendingUp,
  Users,
  MessageSquare,
  Activity,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { apiClient } from "../services/api";

const TABS = [
  { id: "dashboard", icon: Activity },
  { id: "surveys", icon: MessageSquare },
  { id: "create", icon: Plus },
  { id: "respond", icon: Send },
];

const SURVEY_TYPES = [
  { value: "pulse" },
  { value: "enps" },
  { value: "annual" },
  { value: "onboarding" },
];

const surveyTypeLabel = (type, t) => t(`engagementHub.surveyTypes.${type}`, { defaultValue: type });

const statusBadge = (s, t) => {
  const m = {
    draft: { label: t("engagementHub.status.draft"), variant: "outline" },
    active: { label: t("engagementHub.status.active"), variant: "default" },
    closed: { label: t("engagementHub.status.closed"), variant: "secondary" },
  };
  const d = m[s] || { label: s, variant: "outline" };
  return <Badge variant={d.variant}>{d.label}</Badge>;
};

const enpsColor = (score) => {
  if (score === null || score === undefined) return "text-muted-foreground";
  if (score >= 30) return "text-green-600";
  if (score >= 0) return "text-yellow-600";
  return "text-red-600";
};

// ---------------------------------------------------------------------------
// Tab: Dashboard
// ---------------------------------------------------------------------------
function DashboardTab() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get("v1/engagement/dashboard");
      setData(r.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!data) return (
    <div className="flex items-center gap-2 text-muted-foreground text-sm py-8">
      <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
      {t("engagementHub.loading")}
    </div>
  );

  const kpis = [
    { label: t("engagementHub.dashboard.kpiTotalSurveys"), value: data.total_surveys, icon: MessageSquare },
    { label: t("engagementHub.dashboard.kpiActive"), value: data.active_surveys, icon: Activity },
    { label: t("engagementHub.dashboard.kpiTotalResponses"), value: data.total_responses, icon: Users },
    {
      label: "eNPS",
      value: data.latest_enps !== null && data.latest_enps !== undefined
        ? data.latest_enps
        : "N/A",
      icon: TrendingUp,
      colored: true,
      score: data.latest_enps,
    },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {kpis.map((k) => (
          <div key={k.label} className="p-4 border rounded-lg bg-card">
            <div className="flex items-center gap-2 mb-1">
              <k.icon className="h-4 w-4 text-muted-foreground" />
              <p className="text-xs text-muted-foreground">{k.label}</p>
            </div>
            <p className={`text-2xl font-bold ${k.colored ? enpsColor(k.score) : ""}`}>
              {k.value}
            </p>
          </div>
        ))}
      </div>

      <div>
        <h3 className="text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wide">
          {t("engagementHub.dashboard.recentSurveys")}
        </h3>
        {data.recent_surveys?.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("engagementHub.dashboard.emptyRecent")}</p>
        ) : (
          <div className="space-y-2">
            {data.recent_surveys?.map((s) => (
              <div key={s.id} className="flex items-center justify-between p-3 border rounded-lg bg-card">
                <div>
                  <p className="text-sm font-medium">{s.title}</p>
                  <p className="text-xs text-muted-foreground">
                    {surveyTypeLabel(s.survey_type, t)}
                    {" · "}
                    {t("engagementHub.common.responseCount", { count: s.response_count })}
                  </p>
                </div>
                {statusBadge(s.status, t)}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Surveys list
// ---------------------------------------------------------------------------
function SurveysTab({ onAnalyze }) {
  const { t } = useTranslation();
  const [surveys, setSurveys] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [analytics, setAnalytics] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get("v1/engagement/surveys");
      setSurveys(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch {
      setSurveys([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    await apiClient.patch(`v1/engagement/surveys/${id}`, { status });
    load();
  };

  const deleteSurvey = async (id) => {
    if (!window.confirm(t("engagementHub.surveys.confirmDelete"))) return;
    await apiClient.delete(`v1/engagement/surveys/${id}`);
    load();
  };

  const loadAnalytics = async (id) => {
    if (analytics[id]) return;
    try {
      const r = await apiClient.get(`v1/engagement/surveys/${id}/analytics`);
      setAnalytics((prev) => ({ ...prev, [id]: r.data }));
    } catch {
      setAnalytics((prev) => ({ ...prev, [id]: { error: true } }));
    }
  };

  const toggle = async (id) => {
    if (expanded === id) { setExpanded(null); return; }
    setExpanded(id);
    await loadAnalytics(id);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">{t("engagementHub.surveys.count", { count: total })}</span>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      {surveys.length === 0 ? (
        <p className="text-center text-sm text-muted-foreground py-8">{t("engagementHub.surveys.empty")}</p>
      ) : (
        <div className="space-y-2">
          {surveys.map((s) => {
            const anal = analytics[s.id];
            return (
              <div key={s.id} className="border rounded-lg bg-card overflow-hidden">
                <div
                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/30"
                  onClick={() => toggle(s.id)}
                >
                  <div className="flex items-center gap-3">
                    {expanded === s.id ? (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    )}
                    <div>
                      <p className="font-medium text-sm">{s.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {surveyTypeLabel(s.survey_type, t)}
                        {" · "}
                        {t("engagementHub.common.responseCount", { count: s.response_count })}
                        {s.enps_score !== null && s.enps_score !== undefined && (
                          <span className={`ml-2 font-semibold ${enpsColor(s.enps_score)}`}>
                            eNPS {s.enps_score}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {statusBadge(s.status, t)}
                  </div>
                </div>

                {expanded === s.id && (
                  <div className="border-t px-4 pb-4 pt-3 bg-muted/10 space-y-4">
                    {/* Actions */}
                    <div className="flex gap-2 flex-wrap">
                      {s.status === "draft" && (
                        <Button size="sm" variant="outline" onClick={() => setStatus(s.id, "active")}>
                          {t("engagementHub.surveys.activate")}
                        </Button>
                      )}
                      {s.status === "active" && (
                        <Button size="sm" variant="outline" onClick={() => setStatus(s.id, "closed")}>
                          {t("engagementHub.surveys.close")}
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => deleteSurvey(s.id)}>
                        <Trash2 className="h-3.5 w-3.5 mr-1" /> {t("engagementHub.surveys.delete")}
                      </Button>
                    </div>

                    {/* Analytics */}
                    {anal && !anal.error && (
                      <div className="space-y-3">
                        {anal.nps_breakdown && (
                          <div className="grid grid-cols-3 gap-3 text-center">
                            {[
                              { label: t("engagementHub.surveys.promoters"), val: anal.nps_breakdown.promoters_pct + "%", cls: "text-green-600" },
                              { label: t("engagementHub.surveys.passives"), val: anal.nps_breakdown.passives !== undefined ? (100 - anal.nps_breakdown.promoters_pct - anal.nps_breakdown.detractors_pct).toFixed(1) + "%" : "—", cls: "" },
                              { label: t("engagementHub.surveys.detractors"), val: anal.nps_breakdown.detractors_pct + "%", cls: "text-red-600" },
                            ].map((k) => (
                              <div key={k.label} className="p-2 border rounded">
                                <p className={`text-lg font-bold ${k.cls}`}>{k.val}</p>
                                <p className="text-xs text-muted-foreground">{k.label}</p>
                              </div>
                            ))}
                          </div>
                        )}
                        {Object.values(anal.question_stats || {}).map((qs, i) => (
                          <div key={i} className="text-sm">
                            <p className="font-medium text-muted-foreground">{qs.question}</p>
                            {qs.avg !== undefined && (
                              <p>{t("engagementHub.surveys.average")} <strong>{qs.avg}</strong> ({t("engagementHub.common.responseCount", { count: qs.count })})</p>
                            )}
                            {qs.distribution && (
                              <div className="flex flex-wrap gap-2 mt-1">
                                {Object.entries(qs.distribution).map(([k, v]) => (
                                  <span key={k} className="px-2 py-0.5 bg-muted rounded text-xs">{k}: {v}</span>
                                ))}
                              </div>
                            )}
                            {qs.yes_pct !== undefined && (
                              <p>{t("engagementHub.surveys.yes")} <strong>{qs.yes_pct}%</strong> ({t("engagementHub.common.responseCount", { count: qs.count })})</p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Create survey
// ---------------------------------------------------------------------------
const QUESTION_TYPES = [
  { value: "rating_1_10" },
  { value: "rating_1_5" },
  { value: "nps" },
  { value: "text" },
  { value: "multiple_choice" },
  { value: "yes_no" },
];

const questionTypeLabel = (type, t) => t(`engagementHub.questionTypes.${type}`, { defaultValue: type });

function CreateTab({ onCreated }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    title: "",
    description: "",
    survey_type: "pulse",
    is_anonymous: true,
    target_all: true,
  });
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const addQuestion = () => {
    setQuestions((prev) => [
      ...prev,
      { id: String(Date.now()), text: "", type: "rating_1_10", required: true, options: [] },
    ]);
  };

  const updateQuestion = (idx, field, val) => {
    setQuestions((prev) => prev.map((q, i) => i === idx ? { ...q, [field]: val } : q));
  };

  const removeQuestion = (idx) => {
    setQuestions((prev) => prev.filter((_, i) => i !== idx));
  };

  const submit = async () => {
    if (!form.title.trim()) return;
    setLoading(true);
    try {
      await apiClient.post("v1/engagement/surveys", {
        ...form,
        questions,
      });
      setSuccess(true);
      setTimeout(() => { setSuccess(false); onCreated?.(); }, 1500);
      setForm({ title: "", description: "", survey_type: "pulse", is_anonymous: true, target_all: true });
      setQuestions([]);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 max-w-xl">
      {success && (
        <div className="flex items-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded-lg p-3 text-sm">
          <CheckCircle className="h-4 w-4" /> {t("engagementHub.create.success")}
        </div>
      )}

      <div className="space-y-3">
        <div>
          <label className="text-sm font-medium">{t("engagementHub.create.fieldTitle")}</label>
          <input
            className="mt-1 w-full border rounded-md px-3 py-2 text-sm bg-background"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder={t("engagementHub.create.titlePlaceholder")}
          />
        </div>
        <div>
          <label className="text-sm font-medium">{t("engagementHub.create.fieldDescription")}</label>
          <textarea
            className="mt-1 w-full border rounded-md px-3 py-2 text-sm bg-background resize-none"
            rows={2}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-sm font-medium">{t("engagementHub.create.fieldType")}</label>
            <select
              className="mt-1 w-full border rounded-md px-3 py-2 text-sm bg-background"
              value={form.survey_type}
              onChange={(e) => setForm({ ...form, survey_type: e.target.value })}
            >
              {SURVEY_TYPES.map((type) => <option key={type.value} value={type.value}>{surveyTypeLabel(type.value, t)}</option>)}
            </select>
          </div>
          <div className="flex items-end gap-4 pb-0.5">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_anonymous}
                onChange={(e) => setForm({ ...form, is_anonymous: e.target.checked })}
              />
              {t("engagementHub.create.anonymous")}
            </label>
          </div>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold">{t("engagementHub.create.questions")}</h3>
          <Button variant="outline" size="sm" onClick={addQuestion}>
            <Plus className="h-4 w-4 mr-1" /> {t("engagementHub.create.addQuestion")}
          </Button>
        </div>
        {questions.length === 0 && (
          <p className="text-xs text-muted-foreground">{t("engagementHub.create.noQuestions")}</p>
        )}
        <div className="space-y-3">
          {questions.map((q, idx) => (
            <div key={q.id} className="p-3 border rounded-lg space-y-2">
              <div className="flex gap-2">
                <input
                  className="flex-1 border rounded px-2 py-1 text-sm bg-background"
                  placeholder={t("engagementHub.create.questionPlaceholder")}
                  value={q.text}
                  onChange={(e) => updateQuestion(idx, "text", e.target.value)}
                />
                <select
                  className="border rounded px-2 py-1 text-sm bg-background"
                  value={q.type}
                  onChange={(e) => updateQuestion(idx, "type", e.target.value)}
                >
                  {QUESTION_TYPES.map((type) => <option key={type.value} value={type.value}>{questionTypeLabel(type.value, t)}</option>)}
                </select>
                <Button variant="ghost" size="sm" onClick={() => removeQuestion(idx)}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Button onClick={submit} disabled={loading || !form.title.trim()}>
        {loading ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
        {t("engagementHub.create.createSurvey")}
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Respond
// ---------------------------------------------------------------------------
function RespondTab() {
  const { t } = useTranslation();
  const [surveys, setSurveys] = useState([]);
  const [selected, setSelected] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    apiClient.get("v1/engagement/surveys?status=active").then((r) => {
      setSurveys(r.data.items || []);
    });
  }, []);

  const setAnswer = (qid, val) => setAnswers((prev) => ({ ...prev, [qid]: val }));

  const submit = async () => {
    if (!selected) return;
    setLoading(true);
    try {
      await apiClient.post(`v1/engagement/surveys/${selected.id}/respond`, { answers });
      setSubmitted(true);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="flex flex-col items-center gap-3 py-12 text-center">
        <CheckCircle className="h-10 w-10 text-green-500" />
        <p className="text-lg font-semibold">{t("engagementHub.respond.thanks")}</p>
        <p className="text-sm text-muted-foreground">{t("engagementHub.respond.thanksHint")}</p>
        <Button variant="outline" size="sm" onClick={() => { setSubmitted(false); setSelected(null); setAnswers({}); }}>
          {t("engagementHub.respond.answerAnother")}
        </Button>
      </div>
    );
  }

  if (!selected) {
    return (
      <div className="space-y-3 max-w-xl">
        <p className="text-sm text-muted-foreground">{t("engagementHub.respond.available")}</p>
        {surveys.length === 0 && (
          <p className="text-sm text-muted-foreground py-4">{t("engagementHub.respond.noActive")}</p>
        )}
        {surveys.map((s) => (
          <div
            key={s.id}
            className="p-4 border rounded-lg bg-card cursor-pointer hover:bg-muted/30 flex items-center justify-between"
            onClick={() => { setSelected(s); setAnswers({}); }}
          >
            <div>
              <p className="font-medium text-sm">{s.title}</p>
              <p className="text-xs text-muted-foreground">
                {surveyTypeLabel(s.survey_type, t)} · {t("engagementHub.respond.questionCount", { count: s.questions?.length })}
                {s.is_anonymous && ` · ${t("engagementHub.create.anonymous")}`}
              </p>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-xl">
      <div>
        <Button variant="ghost" size="sm" onClick={() => setSelected(null)} className="-ml-2 mb-2">
          {t("engagementHub.respond.back")}
        </Button>
        <h2 className="text-lg font-semibold">{selected.title}</h2>
        {selected.description && <p className="text-sm text-muted-foreground mt-1">{selected.description}</p>}
        {selected.is_anonymous && (
          <p className="text-xs text-blue-600 mt-1">{t("engagementHub.respond.anonymousResponses")}</p>
        )}
      </div>

      <div className="space-y-5">
        {(selected.questions || []).map((q) => (
          <div key={q.id} className="space-y-2">
            <label className="text-sm font-medium">
              {q.text}
              {q.required && <span className="text-red-500 ml-1">*</span>}
            </label>

            {(q.type === "rating_1_10" || q.type === "rating_1_5" || q.type === "nps") && (
              <div className="flex gap-2 flex-wrap">
                {Array.from({ length: q.type === "rating_1_5" ? 5 : 10 }, (_, i) => i + (q.type === "nps" ? 0 : 1)).map((n) => (
                  <button
                    key={n}
                    onClick={() => setAnswer(q.id, n)}
                    className={`w-9 h-9 rounded-lg border text-sm font-medium transition-colors ${
                      answers[q.id] === n
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-background hover:bg-muted"
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            )}

            {q.type === "text" && (
              <textarea
                className="w-full border rounded-md px-3 py-2 text-sm bg-background resize-none"
                rows={3}
                value={answers[q.id] || ""}
                onChange={(e) => setAnswer(q.id, e.target.value)}
              />
            )}

            {q.type === "yes_no" && (
              <div className="flex gap-3">
                {["yes", "no"].map((v) => (
                  <button
                    key={v}
                    onClick={() => setAnswer(q.id, v)}
                    className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                      answers[q.id] === v ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"
                    }`}
                  >
                    {v === "yes" ? t("engagementHub.respond.yes") : t("engagementHub.respond.no")}
                  </button>
                ))}
              </div>
            )}

            {q.type === "multiple_choice" && q.options?.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {q.options.map((opt) => (
                  <button
                    key={opt}
                    onClick={() => setAnswer(q.id, opt)}
                    className={`px-3 py-1.5 rounded-lg border text-sm transition-colors ${
                      answers[q.id] === opt ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"
                    }`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <Button onClick={submit} disabled={loading}>
        {loading ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
        {t("engagementHub.respond.submit")}
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Hub
// ---------------------------------------------------------------------------
export default function EngagementHub() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "dashboard";
  const [refreshKey, setRefreshKey] = useState(0);

  const setTab = (id) => setSearchParams({ tab: id });

  const renderContent = () => {
    switch (activeTab) {
      case "dashboard": return <DashboardTab key={refreshKey} />;
      case "surveys":   return <SurveysTab />;
      case "create":    return <CreateTab onCreated={() => { setRefreshKey((k) => k + 1); setTab("surveys"); }} />;
      case "respond":   return <RespondTab />;
      default:          return <DashboardTab />;
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Heart className="h-6 w-6 text-primary" />
          {t("engagementHub.title")}
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          {t("engagementHub.subtitle")}
        </p>
      </div>

      <div className="border-b flex gap-1">
        {TABS.map(({ id, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === id
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            <Icon className="h-4 w-4" />
            {t(`engagementHub.tabs.${id}`)}
          </button>
        ))}
      </div>

      <div>{renderContent()}</div>
    </div>
  );
}
